/**
 * Branch Service
 * API client for branch management operations
 */

import API from './api';

class BranchService {
  /**
   * Get all branches for the company
   * @param {Object} filters - Optional filters (isActive, type, search)
   * @returns {Promise<Array>}
   */
  async getBranches(filters = {}) {
    try {
      const queryParams = new URLSearchParams();

      if (filters.isActive !== undefined) {
        queryParams.append('isActive', filters.isActive);
      }
      if (filters.type) {
        queryParams.append('type', filters.type);
      }
      if (filters.search) {
        queryParams.append('search', filters.search);
      }

      const queryString = queryParams.toString();
      const endpoint = `/branch${queryString ? `?${queryString}` : ''}`;

      const response = await API.get(endpoint);
      return response.data.branches || response.data;
    } catch (error) {
      console.error('Get branches failed:', error);
      throw error;
    }
  }

  /**
   * Get a single branch by ID
   * @param {String} branchId - Branch ID
   * @returns {Promise<Object>}
   */
  async getBranchById(branchId) {
    try {
      const response = await API.get(`/branch/${branchId}`);
      return response.data.branch || response.data;
    } catch (error) {
      console.error('Get branch failed:', error);
      throw error;
    }
  }

  /**
   * Create a new branch
   * @param {Object} branchData - Branch data
   * @returns {Promise<Object>}
   */
  async createBranch(branchData) {
    try {
      const response = await API.post('/branch', branchData);
      return response.data.branch || response.data;
    } catch (error) {
      console.error('Create branch failed:', error);
      throw error;
    }
  }

  /**
   * Update a branch
   * @param {String} branchId - Branch ID
   * @param {Object} updates - Updated branch data
   * @returns {Promise<Object>}
   */
  async updateBranch(branchId, updates) {
    try {
      const response = await API.put(`/branch/${branchId}`, updates);
      return response.data.branch || response.data;
    } catch (error) {
      console.error('Update branch failed:', error);
      throw error;
    }
  }

  /**
   * Delete a branch
   * @param {String} branchId - Branch ID
   * @returns {Promise<Object>}
   */
  async deleteBranch(branchId) {
    try {
      const response = await API.delete(`/branch/${branchId}`);
      return response.data;
    } catch (error) {
      console.error('Delete branch failed:', error);
      throw error;
    }
  }

  /**
   * Find branches near a location
   * @param {Number} longitude - Longitude
   * @param {Number} latitude - Latitude
   * @param {Number} maxDistance - Maximum distance in miles (default: 50)
   * @returns {Promise<Array>}
   */
  async findNearbyBranches(longitude, latitude, maxDistance = 50) {
    try {
      const response = await API.get('/branch/nearby', {
        params: { longitude, latitude, maxDistance },
      });
      return response.data.branches || response.data;
    } catch (error) {
      console.error('Find nearby branches failed:', error);
      throw error;
    }
  }

  /**
   * Get branch statistics
   * @returns {Promise<Object>}
   */
  async getBranchStats() {
    try {
      const response = await API.get('/branch/stats');
      return response.data.stats || response.data;
    } catch (error) {
      console.error('Get branch stats failed:', error);
      throw error;
    }
  }
}

const branchService = new BranchService();
export default branchService;
